import {
    Flex,
    Heading,
    Text,
    useColorModeValue,
    Avatar,
    Stack,
    Icon,
    Tag,
    IconButton,
    Tooltip,
    Box,
    Input,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    useDisclosure,
    Textarea,
    useToast,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    FormControl,
    FormLabel,
    Select,
} from '@chakra-ui/react';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {
    FiChevronLeft,
    FiChevronRight,
    FiChevronsLeft,
    FiChevronsRight,
} from 'react-icons/fi';
import '../../theme/solarizedTheme';
import { Loading } from '../../helpers/Loading';
import { customStyles } from '../../helpers/customStyles';
import DownloadStudentReport from './StudentReport';
import { getMateria } from '../../features/materiaSlice';
import { getAllMatriculasByCurso, reset } from '../../features/matriculaSlice';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { FaFileExcel, FaGraduationCap } from 'react-icons/fa';
import { getAllConfiguraciones } from '../../features/configuracionSlice';
import { Check, Edit2 } from 'lucide-react';
import CustomBackRoute from '../../helpers/CustomBackRoute';
import { AddIcon } from '@chakra-ui/icons';
import { getActiveAcademicYear } from '../../features/academicYearSlice';
import { saveAllCalificaciones, getAllNotasPorMateria } from '../../features/notaSlice';

// Constantes
const ESTADOS = {
    PENDIENTE: 'Pendiente',
    APROBADO: 'Aprobado',
    REPROBADO: 'Reprobado',
};

const ESTADO_COLORS = {
    [ESTADOS.APROBADO]: 'green',
    [ESTADOS.REPROBADO]: 'red',
    [ESTADOS.PENDIENTE]: 'gray',
};

const CalificacionCualitativa = () => {
    const dispatch = useDispatch();
    const params = useParams();
    const toast = useToast();
    const themeTable = useColorModeValue('default', 'solarized');

    // Redux selectors
    const { materia } = useSelector(state => state.materias);
    const { notasByMateria, isLoading, isLoadingSave } = useSelector(state => state.calificaciones);
    const { sedeSeleccionada, user } = useSelector(state => state.auth);
    const { configuracion } = useSelector(state => state.configuraciones);
    const { active_academic_year } = useSelector(state => state.academic_year);

    // Local state
    const [search, setSearch] = useState('');
    const [notasData, setNotasData] = useState({});
    const [hasChanges, setHasChanges] = useState(false);

    // Cargar datos iniciales
    useEffect(() => {
        dispatch(getMateria(params.id));
        dispatch(getAllMatriculasByCurso(params.id));
        dispatch(getAllNotasPorMateria({ materia: params.id, grado: materia?.grado?._id }));
        dispatch(getAllConfiguraciones());
        dispatch(getActiveAcademicYear());

        return () => {
            dispatch(reset());
        };
    }, [dispatch, params.id, sedeSeleccionada._id, materia?.grado?._id]);

    // Inicializar notasData con datos del servidor
    useEffect(() => {
        if (notasByMateria?.length > 0) {
            const initialData = notasByMateria.reduce((acc, row) => {
                acc[row.matriculaId] = {
                    matriculaId: row.matriculaId,
                    observaciones: row.observaciones || '',
                    valoraciones: row.valoraciones || [],
                    estado: row.estado || ESTADOS.PENDIENTE,
                };
                return acc;
            }, {});
            setNotasData(initialData);
        }
    }, [notasByMateria]);

    // Verificar cambios
    const hasDataChanged = useMemo(() => {
        return Object.entries(notasData).some(([key, values]) => {
            const originalRow = notasByMateria.find(row => row.matriculaId === key);
            if (!originalRow) return true;

            return (
                values.observaciones !== (originalRow.observaciones || '') ||
                values.estado !== (originalRow.estado || ESTADOS.PENDIENTE) ||
                JSON.stringify(values.valoraciones) !== JSON.stringify(originalRow.valoraciones || [])
            );
        });
    }, [notasData, notasByMateria]);

    // Guardar todos los cambios
    const handleSaveAll = async () => {
        const changedData = Object.entries(notasData)
            .filter(([key, values]) => {
                const originalRow = notasByMateria.find(row => row.matriculaId === key);
                if (!originalRow) return true;

                return (
                    values.observaciones !== (originalRow.observaciones || '') ||
                    values.estado !== (originalRow.estado || ESTADOS.PENDIENTE) ||
                    JSON.stringify(values.valoraciones) !== JSON.stringify(originalRow.valoraciones || [])
                );
            })
            .map(([key, values]) => ({
                matricula: values.matriculaId || key,
                materia: params.id,
                profesor: user?.usuario?.id || null,
                observaciones: values.observaciones || '',
                estado: values.estado || ESTADOS.PENDIENTE,
                valoraciones: values.valoraciones || [],
            }));

        if (changedData.length === 0) {
            toast({
                title: 'Sin cambios',
                description: 'No hay modificaciones que guardar',
                status: 'info',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            const result = await dispatch(saveAllCalificaciones({ notas: changedData })).unwrap();

            toast({
                title: 'Éxito',
                description: `Se guardaron ${changedData.length} registro(s) correctamente`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            if (result.errores?.length > 0) {
                toast({
                    title: 'Advertencias',
                    description: `${result.errores.length} registro(s) con errores`,
                    status: 'warning',
                    duration: 5000,
                    isClosable: true,
                });
            }

            setHasChanges(false);
            dispatch(getAllNotasPorMateria({ materia: params.id, grado: materia?.grado?._id }));

        } catch (error) {
            toast({
                title: 'Error',
                description: error.message || 'Error al guardar las calificaciones',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    // Actualizar un campo específico
    const updateNotaField = (matriculaId, field, value) => {
        setNotasData(prev => ({
            ...prev,
            [matriculaId]: {
                ...prev[matriculaId],
                matriculaId,
                [field]: value,
            },
        }));
        setHasChanges(true);
    };

    // Limpiar cambios
    const handleClearChanges = () => {
        const initialData = notasByMateria.reduce((acc, row) => {
            acc[row.matriculaId] = {
                matriculaId: row.matriculaId,
                observaciones: row.observaciones || '',
                valoraciones: row.valoraciones || [],
                estado: row.estado || ESTADOS.PENDIENTE,
            };
            return acc;
        }, {});
        setNotasData(initialData);
        setHasChanges(false);
        
        toast({
            title: 'Cambios descartados',
            description: 'Se han revertido todos los cambios pendientes',
            status: 'info',
            duration: 3000,
            isClosable: true,
        });
    };

    // Filtrar datos
    const filteredData = useMemo(() => {
        return notasByMateria.filter(row =>
            `${row.estudiante?.nombres} ${row.estudiante?.apellidos}`
                .toLowerCase()
                .includes(search.toLowerCase())
        );
    }, [notasByMateria, search]);

    // Exportar a Excel
    const handleExportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(
            notasByMateria.map(row => ({
                Estudiante: `${row.estudiante?.nombres} ${row.estudiante?.apellidos}`,
                Grado: row.grado?.nombre,
                Estado: row.estado || 'N/A',
                Observaciones: row.observaciones || '',
            }))
        );

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Estudiantes');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(data, `${materia.nombre}.xlsx`);
    };

    // Columnas de la tabla
    const columns = useMemo(() => [
        {
            name: 'N',
            selector: row => notasByMateria?.indexOf(row) + 1,
            sortable: true,
            width: '70px',
        },
        {
            name: 'ESTUDIANTE',
            selector: row => `${row.estudiante?.nombres} ${row.estudiante?.apellidos}`,
            sortable: true,
            cell: row => (
                <Stack spacing={2} direction="row" align="center">
                    <Avatar
                        size="sm"
                        name={`${row.estudiante?.nombres} ${row.estudiante?.apellidos}`}
                        src={row?.img}
                        display={{ base: 'none', lg: 'flex' }}
                    />
                    <Text fontSize="12px" noOfLines={2}>
                        {row.estudiante?.nombres} {row.estudiante?.apellidos}
                    </Text>
                </Stack>
            ),
        },
        {
            name: 'VALORACIÓN',
            cell: row => (
                <ModalValoraciones
                    data={row}
                    notasData={notasData}
                    onUpdate={updateNotaField}
                />
            ),
            center: true,
        },
        {
            name: 'OBSERVACIONES',
            cell: row => (
                <ModalObservaciones
                    data={row}
                    notasData={notasData}
                    onUpdate={updateNotaField}
                />
            ),
            center: true,
        },
        {
            name: 'ESTADO',
            cell: row => (
                <ModalEstado
                    data={row}
                    notasData={notasData}
                    onUpdate={updateNotaField}
                />
            ),
            center: true,
        }
    ], [materia, notasData, configuracion]);

    if (isLoading) {
        return <Loading />;
    }

    return (
        <Box
            borderTop="8px solid"
            borderColor={materia.brand_color}
            borderRadius="2xl"
            bg="white"
            boxShadow="base"
            _dark={{ bg: 'primary.1000', color: 'white' }}
            p={6}
        >
            {/* Header */}
            <Flex justify="space-between" align="center" mb={4}>
                <CustomBackRoute />
                <Stack spacing={2} ml={2} w="full" direction="row" justify="space-between" align="center">
                    <Heading size="md">{materia.nombre}</Heading>
                    <Tag colorScheme="purple" px={4} variant="outline">
                        <Icon as={FaGraduationCap} mr={2} boxSize={4} />
                        GRADO: {materia?.grado?.nombre}
                    </Tag>
                </Stack>
            </Flex>

            {/* Search and actions */}
            <Stack direction={['column', 'column', 'row']} justify="space-between" spacing={4} mb={4}>
                <Input
                    placeholder="Buscar estudiante..."
                    variant="filled"
                    size="lg"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    focusBorderColor="primary.100"
                />
                <Stack direction="row" spacing={2}>
                    <Tooltip label="Exportar a Excel">
                        <IconButton
                            icon={<FaFileExcel size={24} />}
                            size="lg"
                            colorScheme="green"
                            onClick={handleExportExcel}
                        />
                    </Tooltip>
                    <DownloadStudentReport id={params.id} />
                </Stack>
            </Stack>

            {/* Alert cambios pendientes */}
            {hasDataChanged && (
                <Alert status="info" mb={4} borderRadius="md">
                    <AlertIcon />
                    <AlertTitle>Cambios pendientes</AlertTitle>
                    <AlertDescription>
                        Tienes modificaciones sin guardar. Haz clic en "GUARDAR CAMBIOS".
                    </AlertDescription>
                </Alert>
            )}

            {/* Period info and actions */}
            <Stack mb={4} direction={['column', 'row']} justify="space-between" align="center">
                <Heading size="sm" fontWeight="normal">
                    PERIODO ACADÉMICO: 
                    <Text as="span" fontWeight="bold" ml={2}>
                        {active_academic_year?.year} - {active_academic_year?.periodo}
                    </Text>
                </Heading>
                <Stack direction="row" spacing={2}>
                    <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={handleClearChanges}
                        isDisabled={!hasDataChanged}
                    >
                        DESCARTAR CAMBIOS
                    </Button>
                    <Button
                        size="sm"
                        colorScheme="primary"
                        onClick={handleSaveAll}
                        isLoading={isLoadingSave}
                        loadingText="Guardando..."
                        isDisabled={!hasDataChanged}
                    >
                        GUARDAR CAMBIOS
                    </Button>
                </Stack>
            </Stack>

            {/* Data table */}
            <DataTable
                columns={columns}
                data={filteredData}
                pagination
                paginationIconFirstPage={<Icon as={FiChevronsLeft} boxSize={6} />}
                paginationIconLastPage={<Icon as={FiChevronsRight} boxSize={6} />}
                paginationIconPrevious={<Icon as={FiChevronLeft} boxSize={6} />}
                paginationIconNext={<Icon as={FiChevronRight} boxSize={6} />}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                paginationPerPage={100}
                paginationComponentOptions={{
                    rowsPerPageText: 'Filas por página:',
                    rangeSeparatorText: 'de',
                    selectAllRowsItem: true,
                    selectAllRowsItemText: 'Todos',
                }}
                theme={themeTable}
                customStyles={customStyles}
                responsive
                noDataComponent={<Text my={4} fontSize="lg">NO HAY REGISTROS</Text>}
            />
        </Box>
    );
};

export default CalificacionCualitativa;

// Componente Modal de Valoraciones
function ModalValoraciones({ data, notasData, onUpdate }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [valoracionText, setValoracionText] = useState('');
    const { active_academic_year } = useSelector(state => state.academic_year);

    const currentPeriodo = Number(active_academic_year?.periodo) || 1;

    useEffect(() => {
        if (isOpen) {
            const valoracionDelPeriodo = notasData[data.matriculaId]?.valoraciones?.find(
                val => val.periodo === currentPeriodo
            );
            setValoracionText(valoracionDelPeriodo?.valoracion || '');
        }
    }, [isOpen, notasData, data.matriculaId, currentPeriodo]);

    const handleSave = () => {
        const existingValoraciones = notasData[data.matriculaId]?.valoraciones || [];
        const otrosPeriodos = existingValoraciones.filter(val => val.periodo !== currentPeriodo);

        const newValoraciones = valoracionText.trim() 
            ? [...otrosPeriodos, { periodo: currentPeriodo, valoracion: valoracionText.trim() }]
            : otrosPeriodos;

        onUpdate(data.matriculaId, 'valoraciones', newValoraciones);
        onClose();
    };

    const valoracionActual = notasData[data.matriculaId]?.valoraciones?.find(
        val => val.periodo === currentPeriodo
    );
    const hasValoracion = Boolean(valoracionActual?.valoracion?.trim());

    return (
        <>
            <Tooltip label={`${hasValoracion ? 'Editar' : 'Agregar'} valoración - Periodo ${currentPeriodo}`}>
                <IconButton
                    icon={<AddIcon />}
                    onClick={onOpen}
                    colorScheme={hasValoracion ? 'purple' : 'gray'}
                    variant={hasValoracion ? 'solid' : 'outline'}
                    size="sm"
                    isRound
                />
            </Tooltip>

            <Modal isOpen={isOpen} onClose={onClose} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {hasValoracion ? 'Editar' : 'Agregar'} Valoración - Periodo {currentPeriodo}
                        <Text fontSize="sm" color="gray.500" fontWeight="normal">
                            {data.estudiante?.nombres} {data.estudiante?.apellidos}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel>Valoración del Periodo {currentPeriodo}</FormLabel>
                            <Textarea
                                placeholder="Escribe la valoración del estudiante..."
                                value={valoracionText}
                                onChange={e => setValoracionText(e.target.value)}
                                minH="200px"
                                focusBorderColor="purple.400"
                            />
                            <Text fontSize="xs" color="gray.500" mt={2}>
                                Caracteres: {valoracionText.length}
                            </Text>
                        </FormControl>

                        {valoracionText.trim() && (
                            <Box mt={4}>
                                <Text fontSize="sm" fontWeight="semibold" mb={2}>Vista previa:</Text>
                                <Box
                                    p={3}
                                    borderRadius="md"
                                    bg="purple.50"
                                    border="1px"
                                    borderColor="purple.200"
                                    _dark={{ bg: 'purple.900', borderColor: 'purple.600' }}
                                >
                                    <Text fontSize="sm" whiteSpace="pre-wrap">{valoracionText}</Text>
                                </Box>
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="purple" mr={3} onClick={handleSave}>
                            Guardar
                        </Button>
                        <Button variant="outline" onClick={onClose}>Cancelar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

// Componente Modal de Observaciones
function ModalObservaciones({ data, notasData, onUpdate }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [observacionText, setObservacionText] = useState('');

    useEffect(() => {
        if (isOpen) {
            setObservacionText(notasData[data.matriculaId]?.observaciones || '');
        }
    }, [isOpen, notasData, data.matriculaId]);

    const handleSave = () => {
        onUpdate(data.matriculaId, 'observaciones', observacionText.trim());
        onClose();
    };

    const hasObservacion = Boolean(notasData[data.matriculaId]?.observaciones?.trim());

    return (
        <>
            <Tooltip label={`${hasObservacion ? 'Editar' : 'Agregar'} observaciones`}>
                <IconButton
                    icon={<AddIcon />}
                    onClick={onOpen}
                    colorScheme={hasObservacion ? 'blue' : 'gray'}
                    variant={hasObservacion ? 'solid' : 'outline'}
                    size="sm"
                    isRound
                />
            </Tooltip>

            <Modal isOpen={isOpen} onClose={onClose} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {hasObservacion ? 'Editar' : 'Agregar'} Observaciones
                        <Text fontSize="sm" color="gray.500" fontWeight="normal">
                            {data.estudiante?.nombres} {data.estudiante?.apellidos}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel>Observaciones generales</FormLabel>
                            <Textarea
                                placeholder="Escribe las observaciones sobre comportamiento, participación, logros..."
                                value={observacionText}
                                onChange={e => setObservacionText(e.target.value)}
                                minH="200px"
                                focusBorderColor="blue.400"
                            />
                            <Text fontSize="xs" color="gray.500" mt={2}>
                                Caracteres: {observacionText.length}
                            </Text>
                        </FormControl>

                        {observacionText.trim() && (
                            <Box mt={4}>
                                <Text fontSize="sm" fontWeight="semibold" mb={2}>Vista previa:</Text>
                                <Box
                                    p={3}
                                    borderRadius="md"
                                    bg="blue.50"
                                    border="1px"
                                    borderColor="blue.200"
                                    _dark={{ bg: 'blue.900', borderColor: 'blue.600' }}
                                >
                                    <Text fontSize="sm" whiteSpace="pre-wrap">{observacionText}</Text>
                                </Box>
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="blue" mr={3} onClick={handleSave}>
                            Guardar
                        </Button>
                        <Button variant="outline" onClick={onClose}>Cancelar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}

// Componente Modal de Estado
function ModalEstado({ data, notasData, onUpdate }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedEstado, setSelectedEstado] = useState('');

    useEffect(() => {
        if (isOpen) {
            setSelectedEstado(notasData[data.matriculaId]?.estado || ESTADOS.PENDIENTE);
        }
    }, [isOpen, notasData, data.matriculaId]);

    const handleSave = () => {
        onUpdate(data.matriculaId, 'estado', selectedEstado);
        onClose();
    };

    const currentEstado = notasData[data.matriculaId]?.estado || data.estado || ESTADOS.PENDIENTE;

    return (
        <>
            <Tooltip label="Cambiar estado">
                <Tag
                    size="md"
                    variant="solid"
                    colorScheme={ESTADO_COLORS[currentEstado]}
                    cursor="pointer"
                    onClick={onOpen}
                    _hover={{ opacity: 0.8 }}
                >
                    {currentEstado}
                </Tag>
            </Tooltip>

            <Modal isOpen={isOpen} onClose={onClose} size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        Cambiar Estado
                        <Text fontSize="sm" color="gray.500" fontWeight="normal">
                            {data.estudiante?.nombres} {data.estudiante?.apellidos}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel>Selecciona el estado del estudiante</FormLabel>
                            <Select
                                value={selectedEstado}
                                onChange={e => setSelectedEstado(e.target.value)}
                                size="lg"
                            >
                                <option value={ESTADOS.PENDIENTE}>{ESTADOS.PENDIENTE}</option>
                                <option value={ESTADOS.APROBADO}>{ESTADOS.APROBADO}</option>
                                <option value={ESTADOS.REPROBADO}>{ESTADOS.REPROBADO}</option>
                            </Select>
                        </FormControl>

                        <Box mt={4} p={3} borderRadius="md" bg="gray.50" _dark={{ bg: 'gray.700' }}>
                            <Text fontSize="sm" fontWeight="semibold" mb={2}>
                                Estado actual:
                            </Text>
                            <Tag
                                size="lg"
                                variant="solid"
                                colorScheme={ESTADO_COLORS[selectedEstado]}
                            >
                                {selectedEstado}
                            </Tag>
                        </Box>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="primary" mr={3} onClick={handleSave}>
                            Guardar
                        </Button>
                        <Button variant="outline" onClick={onClose}>Cancelar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}